/**
 * Écrans de questions non scorées du COPSOQ III (partie 1 « profil » et
 * partie 3 « questions médicales ») : types, visibilité des branches,
 * complétude d'un écran, et conversion saisie ↔ réponses stockées.
 *
 * Une question à `condition` n'est posée que si sa question parente
 * (elle-même visible) a reçu la valeur attendue — égalité pour un choix
 * simple, appartenance pour un choix multiple, seuil pour un nombre.
 *
 * La saisie est gardée brute (chaînes, tableau pour le choix multiple) : un
 * champ numérique en cours de frappe n'est pas encore un nombre.
 */

import type { ReponseNonScoreeCopsoq3 } from "../../lib/types";

export type ConditionQuestion = {
  /** `name` de la question parente. */
  question: string;
} & (
  | {
      /** Libellé d'option qui déclenche l'affichage. */
      valeur: string;
    }
  | {
      /** La parente est un nombre strictement supérieur à ce seuil. */
      superieurA: number;
    }
);

interface QuestionEcranBase {
  /** Clé STABLE de la réponse en base — ne pas renommer. */
  name: string;
  question: string;
  /** Sous-titre de la branche : distingue deux questions de même libellé
   * (ex. le SDIS du volontaire et celui du professionnel). */
  rubrique?: string;
  condition?: ConditionQuestion;
  /** Peut rester vide sans bloquer « Suivant ». */
  facultative?: boolean;
}

export type QuestionEcran = QuestionEcranBase &
  (
    | { type: "choix" | "choix-multiple"; options: readonly string[] }
    | { type: "nombre"; min: number; max: number }
    | { type: "texte"; multiligne?: boolean }
    /** Numéro de département : « 2A » / « 2B » interdisent un champ numérique. */
    | { type: "departement" }
  );

export const OUI_NON = ["Oui", "Non"] as const;

export interface EcranQuestions {
  titre: string;
  questions: readonly QuestionEcran[];
}

export type ValeurSaisie = string | string[];
/** Saisie de tous les écrans, par `name` — unique d'une partie à l'autre. */
export type SaisieEcrans = Record<string, ValeurSaisie>;

const DEPARTEMENT = /^(0[1-9]|1\d|2[1-9AB]|[3-8]\d|9[0-5]|97[1-6])$/;

/** « 2a » → « 2A », « 7 » → « 07 ». */
export function normaliserDepartement(saisie: string): string {
  const v = saisie.trim().toUpperCase();
  return /^\d$/.test(v) ? `0${v}` : v;
}

const index = new WeakMap<
  readonly EcranQuestions[],
  Map<string, QuestionEcran>
>();

function indexer(
  ecrans: readonly EcranQuestions[],
): Map<string, QuestionEcran> {
  let parQuestion = index.get(ecrans);
  if (!parQuestion) {
    parQuestion = new Map(
      ecrans.flatMap((e) => e.questions.map((q) => [q.name, q] as const)),
    );
    index.set(ecrans, parQuestion);
  }
  return parQuestion;
}

function conditionRemplie(
  condition: ConditionQuestion,
  parente: QuestionEcran,
  saisie: ValeurSaisie | undefined,
): boolean {
  if ("superieurA" in condition) {
    const n = valeurReponse(parente, saisie);
    return typeof n === "number" && n > condition.superieurA;
  }
  return Array.isArray(saisie)
    ? saisie.includes(condition.valeur)
    : saisie === condition.valeur;
}

/** Une question est posée si sa parente l'est ET remplit la condition. */
export function estVisible(
  question: QuestionEcran,
  saisie: SaisieEcrans,
  ecrans: readonly EcranQuestions[],
): boolean {
  const parQuestion = indexer(ecrans);
  let courante = question;
  while (courante.condition) {
    const parente = parQuestion.get(courante.condition.question);
    if (
      !parente ||
      !conditionRemplie(courante.condition, parente, saisie[parente.name])
    )
      return false;
    courante = parente;
  }
  return true;
}

export function questionsVisibles(
  ecran: EcranQuestions,
  saisie: SaisieEcrans,
  ecrans: readonly EcranQuestions[],
): QuestionEcran[] {
  return ecran.questions.filter((q) => estVisible(q, saisie, ecrans));
}

/** La saisie, convertie en réponse stockable — `undefined` si invalide. */
export function valeurReponse(
  question: QuestionEcran,
  saisie: ValeurSaisie | undefined,
): ReponseNonScoreeCopsoq3["reponse"] | undefined {
  if (saisie === undefined) return undefined;
  switch (question.type) {
    case "choix":
      return typeof saisie === "string" && question.options.includes(saisie)
        ? saisie
        : undefined;
    case "choix-multiple": {
      if (!Array.isArray(saisie)) return undefined;
      // Ordre des options, pas ordre des clics.
      const retenues = question.options.filter((o) => saisie.includes(o));
      return retenues.length > 0 ? retenues : undefined;
    }
    case "nombre": {
      if (typeof saisie !== "string" || !/^\d+$/.test(saisie.trim()))
        return undefined;
      const n = Number(saisie.trim());
      return n >= question.min && n <= question.max ? n : undefined;
    }
    case "texte": {
      if (typeof saisie !== "string") return undefined;
      const texte = saisie.trim();
      return texte === "" ? undefined : texte;
    }
    case "departement": {
      if (typeof saisie !== "string") return undefined;
      const code = normaliserDepartement(saisie);
      return DEPARTEMENT.test(code) ? code : undefined;
    }
  }
}

/** Toutes les questions VISIBLES et obligatoires de l'écran ont une réponse
 * valide. */
export function ecranComplet(
  ecran: EcranQuestions,
  saisie: SaisieEcrans,
  ecrans: readonly EcranQuestions[],
): boolean {
  return questionsVisibles(ecran, saisie, ecrans).every(
    (q) => q.facultative || valeurReponse(q, saisie[q.name]) !== undefined,
  );
}

/**
 * Les réponses d'une partie à enregistrer, dans l'ordre du questionnaire. Les
 * réponses d'une branche refermée (statut décoché, affectation changée) sont
 * écartées : elles ne décrivent plus le répondant.
 */
export function versReponses(
  saisie: SaisieEcrans,
  ecrans: readonly EcranQuestions[],
): ReponseNonScoreeCopsoq3[] {
  return ecrans.flatMap((ecran) =>
    questionsVisibles(ecran, saisie, ecrans).flatMap((q) => {
      const reponse = valeurReponse(q, saisie[q.name]);
      if (reponse === undefined) return [];
      return [
        {
          name: q.name,
          ...(q.rubrique && { rubrique: q.rubrique }),
          question: q.question,
          reponse,
        },
      ];
    }),
  );
}

export function depuisReponses(
  ...parties: (readonly ReponseNonScoreeCopsoq3[] | undefined)[]
): SaisieEcrans {
  return Object.fromEntries(
    parties.flatMap((reponses) =>
      (reponses ?? []).map((r) => [
        r.name,
        typeof r.reponse === "number" ? String(r.reponse) : r.reponse,
      ]),
    ),
  );
}

export type PageCopsoq3 =
  | { partie: 1 | 3; ecran: EcranQuestions; index: number; nb: number }
  | { partie: 2; index: number; nb: number };

/**
 * Ce qu'affiche une page du wizard : les écrans de la partie 1, une question
 * de la partie 2 par page, puis les écrans de la partie 3.
 */
export function situerPage(
  page: number,
  partie1: readonly EcranQuestions[],
  nbQuestions: number,
  partie3: readonly EcranQuestions[],
): PageCopsoq3 | undefined {
  if (page < 0) return undefined;
  if (page < partie1.length)
    return {
      partie: 1,
      ecran: partie1[page]!,
      index: page,
      nb: partie1.length,
    };
  const q = page - partie1.length;
  if (q < nbQuestions) return { partie: 2, index: q, nb: nbQuestions };
  const e = q - nbQuestions;
  const ecran = partie3[e];
  return ecran && { partie: 3, ecran, index: e, nb: partie3.length };
}
